import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Scale, UserCheck, AlertTriangle, Shield, CreditCard, Ban, RefreshCw } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <FileText className="h-16 w-16 text-white mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-white/90">
            Last Updated: December 2024
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4">Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to Wayo. By accessing or using our platform, you agree to be bound by these Terms of Service 
              ("Terms"). Please read them carefully. If you do not agree to these Terms, you may not access or use 
              our services. These Terms constitute a legally binding agreement between you and Wayo.
            </p>
          </CardContent>
        </Card>

        {/* Eligibility */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Eligibility</h2>
            </div>
            <p className="text-gray-700 mb-4">To use Wayo, you must:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Be at least 13 years of age (users under 18 require parental consent)</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Have the legal capacity to enter into this agreement</li>
              <li>Not be prohibited from using our services under applicable law</li>
            </ul>
          </CardContent>
        </Card>

        {/* User Responsibilities */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold">User Responsibilities</h2>
            </div>
            <p className="text-gray-700 mb-4">When using Wayo, you agree to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Provide truthful, accurate, and complete information in your applications</li>
              <li>Not submit false, misleading, or plagiarized content</li>
              <li>Respect the intellectual property rights of others</li>
              <li>Not attempt to gain unauthorized access to our systems</li>
              <li>Not use our platform for any illegal or unauthorized purpose</li>
              <li>Not interfere with or disrupt the platform or servers</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Report any security vulnerabilities or suspicious activity</li>
            </ul>
          </CardContent>
        </Card>

        {/* Application Submission */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-bold">Application Submissions</h2>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>You are solely responsible for the content of your applications</li>
              <li>Submitted applications cannot be modified after the deadline</li>
              <li>Application fees are processed by our payment partners and may be non-refundable</li>
              <li>We do not guarantee admission to any institution</li>
              <li>Colleges make independent admission decisions</li>
              <li>Application materials become the property of recipient institutions</li>
              <li>We reserve the right to refuse submissions that violate our policies</li>
            </ul>
          </CardContent>
        </Card>

        {/* Fees and Payments */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-bold">Fees and Payments</h2>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Creating an account on Wayo is free</li>
              <li>Application fees vary by institution and are set by the colleges</li>
              <li>Fee waivers may be available for eligible students</li>
              <li>Payments are processed securely through our payment partners</li>
              <li>Refunds are subject to individual college policies</li>
              <li>You are responsible for any applicable taxes</li>
            </ul>
          </CardContent>
        </Card>

        {/* Intellectual Property */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold">Intellectual Property</h2>
            </div>
            <p className="text-gray-700 mb-4">
              The Wayo platform, including its design, features, content, and code, is owned by Wayo and 
              protected by intellectual property laws. You may not:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Copy, modify, or distribute our platform without permission</li>
              <li>Use our trademarks or branding without authorization</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Create derivative works based on our platform</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You retain ownership of the content you submit (essays, personal information, etc.) but grant 
              us a license to use it for providing our services.
            </p>
          </CardContent>
        </Card>

        {/* Prohibited Activities */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <Ban className="h-6 w-6 text-red-600" />
              <h2 className="text-2xl font-bold">Prohibited Activities</h2>
            </div>
            <p className="text-gray-700 mb-4">The following activities are strictly prohibited:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Submitting fraudulent or falsified application materials</li>
              <li>Impersonating another person or entity</li>
              <li>Using automated systems to access or interact with the platform</li>
              <li>Attempting to bypass security measures</li>
              <li>Harvesting user data without consent</li>
              <li>Engaging in any form of harassment or discrimination</li>
              <li>Uploading malicious code or viruses</li>
              <li>Using the platform for commercial purposes without authorization</li>
            </ul>
          </CardContent>
        </Card>

        {/* Limitation of Liability */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <h2 className="text-2xl font-bold">Limitation of Liability</h2>
            </div>
            <p className="text-gray-700 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Wayo is provided "as is" without warranties of any kind</li>
              <li>We do not guarantee uninterrupted or error-free service</li>
              <li>We are not liable for admission decisions made by colleges</li>
              <li>We are not responsible for third-party services or websites</li>
              <li>Our total liability shall not exceed amounts paid to us in the past 12 months</li>
              <li>We are not liable for indirect, incidental, or consequential damages</li>
            </ul>
          </CardContent>
        </Card>

        {/* Termination */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCw className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-bold">Termination</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We may suspend or terminate your account if you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Violate these Terms of Service</li>
              <li>Engage in fraudulent or illegal activity</li>
              <li>Provide false information</li>
              <li>Abuse or harass other users or staff</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You may terminate your account at any time by contacting support. Upon termination, 
              your right to use the platform ceases immediately. Certain provisions of these Terms 
              will survive termination.
            </p>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4">Governing Law</h2>
            <p className="text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of the State of 
              Delaware, United States, without regard to its conflict of law provisions. Any disputes 
              arising under these Terms shall be resolved through binding arbitration or in the courts 
              of Delaware.
            </p>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4">Changes to These Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms at any time. We will notify users of material 
              changes via email or through the platform. Your continued use of Wayo after changes 
              constitutes acceptance of the updated Terms. We encourage you to review the Terms periodically.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mb-8 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4">Contact Information</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="text-gray-700">
              <p><strong>Email:</strong> <a href="mailto:legal@wayoedu.com" className="text-emerald-600 hover:underline">legal@wayoedu.com</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@wayoedu.com" className="text-emerald-600 hover:underline">support@wayoedu.com</a></p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Notice */}
        <div className="text-center text-gray-500 text-sm">
          <p>By using Wayo, you acknowledge that you have read and understood these Terms of Service.</p>
          <p className="mt-2">© 2026 Wayo. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
